import React, { useState, useEffect } from 'react';
import { Check, ArrowRight } from 'lucide-react';

const Packages = ({ onBookCall }) => {
  const [starterCurrency, setStarterCurrency] = useState('£380');
  const [multiCamCurrency, setMultiCamCurrency] = useState('£980');
  const [cinemaCurrency, setCinemaCurrency] = useState('£1800');

  useEffect(() => {
    // Detect user location based on timezone
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // US timezones
    const usTimezones = [
      'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
      'America/Phoenix', 'America/Anchorage', 'Pacific/Honolulu', 'America/Detroit',
      'America/Indianapolis', 'America/Kentucky/Louisville', 'America/Kentucky/Monticello',
      'America/North_Dakota/Beulah', 'America/North_Dakota/Center', 'America/North_Dakota/New_Salem'
    ];

    if (usTimezones.some(tz => timezone.includes(tz))) {
      setStarterCurrency('$500');
      setMultiCamCurrency('$1300');
      setCinemaCurrency('$2400');
    } else {
      setStarterCurrency('£380');
      setMultiCamCurrency('£980');
      setCinemaCurrency('£1800');
    }
  }, []);

  const packages = [
    {
      id: 1,
      name: 'Starter Production',
      description: 'Perfect for individual creators and small projects',
      features: [
        'Single camera setup',
        'Basic lighting & audio',
        '2-hour filming session',
        'Basic editing & delivery',
        'Social media formats'
      ],
      highlight: false,
      price: starterCurrency
    },
    {
      id: 2,
      name: 'Multi-Cam Pro',
      description: 'Our most popular package for professional content',
      features: [
        'Multi-camera setup (3+ cameras)',
        'Professional lighting & audio',
        'Full-day filming session',
        'Advanced editing & color grading',
        'All platform formats',
        'Motion graphics & titles'
      ],
      highlight: true,
      price: multiCamCurrency
    },
    {
      id: 3,
      name: 'Cinema / Film Unit',
      description: 'Premium package for high-end productions',
      features: [
        'Cinema-grade camera package',
        'Full lighting & grip equipment',
        'Multi-day production support',
        'Cinematic post-production',
        'Sound design & mixing',
        'Dedicated production team'
      ],
      highlight: false,
      price: cinemaCurrency
    }
  ];

  return (
    <section id="production-packages" className="relative z-10 py-24 sm:py-32 border-t border-white/[0.06]">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 sm:mb-20">
          <div>
            <p className="ltv-eyebrow text-red-500 mb-4">Packages</p>
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-bold text-white leading-[1.05]">
              Production
              <span className="text-gray-500"> packages.</span>
            </h2>
          </div>
          <p className="text-gray-400 max-w-md text-base leading-relaxed">
            Flexible options to match your production needs — from single-camera sessions to full cinema units.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5 items-stretch">
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              className={`relative rounded-2xl border p-7 sm:p-8 flex flex-col transition-all duration-300 ${
                pkg.highlight
                  ? 'border-red-500/50 bg-red-950/20 shadow-2xl shadow-red-600/10'
                  : 'border-white/[0.08] bg-white/[0.03] hover:bg-white/[0.06] hover:border-white/20'
              }`}
            >
              {pkg.highlight && (
                <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 ltv-eyebrow text-white bg-red-600 rounded-full px-4 py-1.5">
                  Most Popular
                </span>
              )}
              <h3 className="font-display text-xl sm:text-2xl font-bold text-white mb-2">{pkg.name}</h3>
              <p className="text-sm text-gray-400 mb-6">{pkg.description}</p>
              <p className="mb-8">
                <span className="text-xs text-gray-500 uppercase tracking-widest block mb-1">Starting from</span>
                <span className="font-display text-4xl font-bold text-white">{pkg.price}</span>
              </p>
              <ul className="space-y-3 mb-8 flex-1">
                {pkg.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="w-5 h-5 rounded-full bg-red-600/15 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="h-3 w-3 text-red-500" />
                    </span>
                    <span className="text-sm text-gray-300">{feature}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={onBookCall}
                className={`w-full inline-flex items-center justify-center gap-2 text-sm font-semibold rounded-full py-3.5 transition-all ${
                  pkg.highlight
                    ? 'bg-red-600 hover:bg-red-500 text-white shadow-lg shadow-red-600/25'
                    : 'bg-white/[0.06] hover:bg-white/[0.12] text-white border border-white/10'
                }`}
              >
                Book This Package
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Packages;
